import { useTranslation } from 'react-i18next';
import type { LoadStatus, LoadStatusOrUnknown } from '@/lib/loadStatus';
import { cn } from '@/lib/utils';

/**
 * The load status pill: pastel background, the same hue dark for the text (the `status.*` tokens,
 * docs/estilo-visual.md), for all 12 `load_status` values. Whole class names on purpose: a class built
 * at runtime is a class Tailwind never generates. A status this portal does not know is a neutral chip
 * with the word "Unknown status": never colour only, never a guessed status.
 */
export const LOAD_STATUS_CLASSES: Record<LoadStatus, string> = {
  potential: 'bg-status-potential-bg text-status-potential-fg',
  booked: 'bg-status-booked-bg text-status-booked-fg',
  dispatched: 'bg-status-dispatched-bg text-status-dispatched-fg',
  accepted: 'bg-status-accepted-bg text-status-accepted-fg',
  atPickup: 'bg-status-at-pickup-bg text-status-at-pickup-fg',
  loaded: 'bg-status-loaded-bg text-status-loaded-fg',
  inTransit: 'bg-status-in-transit-bg text-status-in-transit-fg',
  delivered: 'bg-status-delivered-bg text-status-delivered-fg',
  podReceived: 'bg-status-pod-received-bg text-status-pod-received-fg',
  invoiced: 'bg-status-invoiced-bg text-status-invoiced-fg',
  paid: 'bg-status-paid-bg text-status-paid-fg',
  canceled: 'bg-status-canceled-bg text-status-canceled-fg',
};

const UNKNOWN_CLASSES = 'bg-sunken text-ink-secondary';

export function LoadStatusChip({ status, className }: { status: LoadStatusOrUnknown; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-semibold whitespace-nowrap',
        status === 'unknown' ? UNKNOWN_CLASSES : LOAD_STATUS_CLASSES[status],
        className,
      )}
    >
      {t(`loadStatus.${status}`)}
    </span>
  );
}
